import React from "react";
import { Card, Empty } from "antd";
import { Link } from "react-router-dom";
import urls from "../../../../urls";
import { formatPriceUsd } from "../../../../functions/formatters";
import { PURCHASE_ORDER_STATUS_PENDING_APPROVAL } from "../../../../constants/OrderConstant";
import { usePurchaseOrderInvoices, usePurchaseOrders, usePurchaseOrderShipments } from "../../../../data/hooks";

const TONES = {
  urgent: { color: "#A03225", background: "#FBECEA", border: "#EFC9C4", label: "Urgent" },
  action: { color: "#96601A", background: "#FDF2E3", border: "#EED9B4", label: "Actie" },
  info: { color: "#1B5E9B", background: "#E8F1FA", border: "#BBD6EE", label: "Info" },
  draft: { color: "#6B6A66", background: "#F4F4F2", border: "#E1DFDA", label: "Concept" },
};

/**
 * "Vraagt aandacht" — de openstaande punten uit het inkoopproces, gebundeld op
 * de rapportagepagina.
 *
 * De rapportage liet tot nu toe alleen verkoopcijfers zien; wat er in het
 * inkoopproces bleef liggen (orders die op goedkeuring wachten, openstaande
 * facturen, zendingen die nooit verstuurd zijn, aanbetalingen die nergens
 * tegenover staan) was alleen te vinden door elk overzicht apart langs te gaan.
 * Elke regel linkt door naar het overzicht met het bijbehorende filter.
 */
const AttentionPanel = () => {
  const { orders } = usePurchaseOrders();
  const { invoices } = usePurchaseOrderInvoices();
  const { shipments } = usePurchaseOrderShipments();

  const awaitingApproval = (orders || []).filter((o) => o.status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL);
  const openInvoices = (invoices || []).filter((inv) => (inv.amountDue ?? inv.totalAmount ?? 0) > 0);
  const openInvoiceTotal = openInvoices.reduce((sum, inv) => sum + (inv.amountDue ?? inv.totalAmount ?? 0), 0);
  const draftShipments = (shipments || []).filter((s) => s.status === "draft");
  const unsettledProformas = (invoices || []).filter((inv) => inv.type === "proforma" && !inv.linkedFinalInvoiceId);
  const unsettledProformaTotal = unsettledProformas.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  const items = [
    awaitingApproval.length > 0 && {
      key: "approval",
      tone: "action",
      to: `${urls.purchaseOrdersList}?status=${PURCHASE_ORDER_STATUS_PENDING_APPROVAL}`,
      title: `${awaitingApproval.length} inkooporder${awaitingApproval.length === 1 ? "" : "s"} ${
        awaitingApproval.length === 1 ? "wacht" : "wachten"
      } op goedkeuring`,
      detail: [...new Set(awaitingApproval.map((o) => o.supplierName).filter(Boolean))].slice(0, 2).join(", "),
    },
    openInvoices.length > 0 && {
      key: "invoices",
      tone: "action",
      to: urls.purchaseOrderInvoicesList,
      title: `${openInvoices.length} factu${openInvoices.length === 1 ? "ur" : "ren"} openstaand`,
      detail: formatPriceUsd(openInvoiceTotal),
    },
    unsettledProformas.length > 0 && {
      key: "proformas",
      tone: "info",
      to: `${urls.purchaseOrderInvoicesList}?type=proforma`,
      title: `${unsettledProformas.length} aanbetaling${unsettledProformas.length === 1 ? "" : "en"} nog niet verrekend`,
      detail: `${formatPriceUsd(unsettledProformaTotal)} · hangt nog niet aan een definitieve factuur`,
    },
    draftShipments.length > 0 && {
      key: "shipments",
      tone: "draft",
      to: `${urls.purchaseOrderShipmentsList}?status=draft`,
      title: `${draftShipments.length} zending${draftShipments.length === 1 ? "" : "en"} in concept`,
      detail: "Nog niet verstuurd naar de leverancier",
    },
  ].filter(Boolean);

  return (
    <Card
      style={{ height: "100%" }}
      styles={{ body: { padding: 0 } }}
      title={<span style={{ fontSize: 15, fontWeight: 500 }}>Vraagt aandacht</span>}
      extra={
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            fontSize: 11,
            color: "rgba(0, 0, 0, 0.4)",
          }}
        >
          {items.length}
        </span>
      }
    >
      {items.length === 0 ? (
        <div style={{ padding: "32px 20px" }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Niets dat aandacht vraagt" />
        </div>
      ) : (
        items.map((item, index) => {
          const tone = TONES[item.tone];
          return (
            <Link
              key={item.key}
              to={item.to}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 18px",
                borderBottom: index === items.length - 1 ? undefined : "1px solid rgba(5, 5, 5, 0.04)",
                color: "inherit",
              }}
            >
              <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{item.title}</span>
                {item.detail ? (
                  <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>{item.detail}</span>
                ) : null}
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: tone.color,
                  background: tone.background,
                  border: `1px solid ${tone.border}`,
                  borderRadius: 4,
                  padding: "2px 7px",
                  whiteSpace: "nowrap",
                  flex: "none",
                }}
              >
                {tone.label}
              </span>
            </Link>
          );
        })
      )}
    </Card>
  );
};

export default AttentionPanel;
